/**
 * Picking photographs out of Google Photos, in one tap, on a computer or a phone.
 *
 * Google retired the Photos Library API's scopes on 31 March 2025, so this
 * uses the Photos Picker API: an app never sees the library, only the photos
 * a person deliberately hands over. We ask Google for a session, send the
 * gardener to Google's own picker with the address it gives us, and ask
 * Google until they have chosen. The steps that need no window are in
 * googlePhotosFlow.
 *
 * This file keeps the pick itself (the class: a tap, the session, bringing a
 * pick in, cancelling and letting go). Its parts are beside it since W1.18
 * part 3 (2026-10-26), moved word for word: googlePhotosSignIn (waiting for
 * the sign-in's handoff, a stranded sign-in failure), googlePhotosSession
 * (the pick's types, this tab's id, the Sentry report) and googlePhotosPicker
 * (the window, the wait between asks, the download). The types the dialogs
 * use are passed on from here, so importers still take everything from this
 * file.
 *
 * One tap, one window. A browser lets a tap open one window, and only before
 * anything is awaited. So the tap opens the window at once, and everything
 * else happens in it:
 *
 * - With a token already held, the window opens blank ("Opening Google
 *   Photos…"), the session is made, and the window is sent to the picker.
 * - Without one, the window opens on Google's sign-in, which sends it back to
 *   google-photos.html (googlePhotosReturn). That page makes the session,
 *   leaves it for this tab in localStorage, and goes on to the picker.
 *
 * It used to sign in through Identity Services' popup, which used the tap, so
 * the picker waited for a second one. And it took the picker's window reading
 * as closed for the gardener giving up — which photos.google.com's
 * Cross-Origin-Opener-Policy makes it read at once, and which a phone makes
 * it read whenever the picker goes to a new tab or the Google Photos app. The
 * gardener cancels with Cancel instead, or the session runs out.
 *
 * The picker's address ends in /autoclose, so Google closes its window on
 * Done and a phone lands back in Grow, where the wait wakes at once.
 *
 * A pick outlives the tab and the dialog (2026-10-11). On Android, Chrome
 * puts Grow's tab away while the gardener is in Google Photos (often the
 * Google Photos app), and may freeze it, throw it away and reload it; Back to
 * Grow on the window may open Grow afresh in the window's own tab; and a
 * dialog closed meanwhile cancelled the pick. The waiting lived only in the
 * tab's memory, so each of those ended it, and the photos chosen never came
 * (the backend never saw POST /google-photos/media). Now the pick handed
 * over is kept in localStorage (googlePhotosFlow's PendingPick) until its
 * photos are in. resume() — called by GooglePhotosResume when the app opens
 * and whenever it comes back to the front, and by a tap's own pick once its
 * session exists — asks Google about it, fetches the photos, and hands them
 * to the dialog that asked when it is still open in this tab, or else to the
 * fallback (GooglePhotosResume: where the pick was going, with a toast). One tab
 * brings a pick in at a time (claimPending). Only Cancel, a new tap or the
 * pick running out ends one early. Every failure but a cancel is sent to
 * Sentry with its reason and the step it stopped at.
 *
 * The bytes come back through our own server (routes/googlePhotos.js in the
 * backend): Google does not promise CORS headers on a `baseUrl`.
 *
 * Requests: Google's own (OAuth sign-in, the Picker API's sessions and
 * media items; googlePhotosFlow) with the gardener's Google token, never
 * sent to our backend except to fetch the bytes:
 * POST /google-photos/media { baseUrl, accessToken } → the image (a Blob),
 * through apiClient (Firebase token, and X-Grow-Garden). The token is held
 * in memory, and in the pending pick in localStorage until its photos are in
 * or it runs out (shortly before Google's own expiry at the latest).
 *
 * Who uses it: the photo dialogs' Google Photos row (PhotoSourcePicker,
 * useGooglePhotos), and GooglePhotosResume (resume, setFallback). Needs
 * VITE_GOOGLE_CLIENT_ID; without it isConfigured() is false and the row is
 * not offered.
 *
 * @module GooglePhotosService
 */

import { VITE_GOOGLE_CLIENT_ID } from '@/config/env-vars';
import { getLanguage } from '@/shared/i18n/i18nSetup';
import { GOOGLE_PHOTOS_PENDING_CLAIM_MS } from '@/config/tunables/shared';
import {
  GooglePhotosError,
  buildAuthUrl,
  claimPending,
  clearPending,
  clearRequest,
  createSession,
  deleteSession,
  isPendingExpired,
  listPicked,
  makePending,
  newState,
  pickerAddress,
  pollUntilPicked,
  readPending,
  returnUri,
  writePending,
  writeRequest,
  type Handoff,
  type HeldToken,
  type PendingPick,
  type PickTarget,
  type PickerSession,
} from './googlePhotosFlow';
import {
  QUIET_FALLBACK,
  Stopped,
  TAB_ID,
  boundFetch,
  localStore,
  reasonOf,
  reportFailure,
  type GooglePhotoPick,
  type LivePick,
  type PickFallback,
  type PickProgress,
  type PickStep,
  type Runner,
} from './googlePhotosSession';
import { reportStrandedFailure, waitForHandoff } from './googlePhotosSignIn';
import { closeQuietly, download, openGoogleWindow, showOpening, sleepUntilBack } from './googlePhotosPicker';

export { GooglePhotosError, type GooglePhotosFailure, type PickTarget } from './googlePhotosFlow';
export type { GooglePhotoPick, GooglePhotosStage, PickFallback, PickProgress } from './googlePhotosSession';

/** Picking photographs out of Google Photos, one pick at a time. */
export class GooglePhotosService {
  /** The one service (getInstance). */
  private static instance: GooglePhotosService;
  /** The Google client id the build was given; empty when it has none. */
  private clientId: string;
  /**
   * The last token Google gave, kept until shortly before it runs out. With
   * one in hand a tap goes straight to the picker without the sign-in.
   */
  private token: HeldToken | null = null;
  /** The pick a tap in this tab started, until it is over. */
  private live: LivePick | null = null;
  /** The pending pick this tab is bringing in, if any. */
  private runner: Runner | null = null;
  /** Where photos go with no dialog open for them. */
  private fallback: PickFallback | null = null;
  /** A resume waiting for another tab's hold to lapse. */
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  /** Reads the client id the build was given. */
  private constructor() {
    this.clientId = VITE_GOOGLE_CLIENT_ID || '';
  }

  /**
   * The one service, made on first use.
   *
   * @returns The service.
   */
  public static getInstance(): GooglePhotosService {
    if (!GooglePhotosService.instance) {
      GooglePhotosService.instance = new GooglePhotosService();
    }
    return GooglePhotosService.instance;
  }

  /**
   * Whether this build can reach Google Photos at all.
   *
   * @returns True when the build has a Google client id.
   */
  public isConfigured(): boolean {
    return Boolean(this.clientId);
  }

  /**
   * Sets where photos go when no dialog is open for them.
   *
   * @param fallback - The fallback, or null to drop it.
   */
  public setFallback(fallback: PickFallback | null): void {
    this.fallback = fallback;
  }

  /**
   * Stops a pick: the gardener's Cancel, or a new tap replacing the last
   * pick. Its promise ends with a 'cancelled' error, its pending record is
   * forgotten and Google's session closed.
   *
   * @param which - Only this pick (a dialog cancelling its own); when left
   *   out (a new tap), this tab's pick and any pending pick not already being
   *   fetched.
   */
  public cancel(which?: Promise<GooglePhotoPick[]>): void {
    const live = this.live;
    if (which && (!live || live.promise !== which)) return;
    const store = localStore();
    const pending = store ? readPending(store) : null;
    const runner = this.runner;
    const pickId = live?.pickId ?? pending?.pickId;
    // Photos already coming over are let in, rather than lost to a new tap.
    if (!which && runner?.committed && runner.pickId === pending?.pickId) {
      if (live && live.pickId !== runner.pickId) this.stopLive(live);
      return;
    }

    if (live) this.stopLive(live);
    if (runner && (!which || runner.pickId === pickId)) {
      runner.controller.abort();
      this.runner = null;
    }
    if (store && pending && (!which || pending.pickId === pickId)) {
      clearPending(store, pending.pickId);
      void deleteSession(boundFetch, pending.sessionId, pending.token.value);
    }
  }

  /**
   * Lets a dialog go without stopping its pick: its promise ends now with no
   * photos, and the photos, when they come, go to the fallback (the tray).
   * Called when the dialog closes or leaves the page.
   *
   * @param which - The dialog's pick.
   */
  public detach(which: Promise<GooglePhotoPick[]>): void {
    const live = this.live;
    if (!live || live.promise !== which || live.detached) return;
    live.detached = true;
    this.settle(live, []);
  }

  /**
   * Runs one whole pick from a tap: window, sign-in if needed, session,
   * choosing, fetching.
   *
   * Not async on purpose: everything up to opening the window happens before
   * this returns, inside the tap, so the browser lets the window open. Call
   * it straight from the click handler, before anything is awaited.
   *
   * @param many - Whether to bring back every photo picked, not just the first.
   * @param onProgress - Told as the pick moves on.
   * @param target - Where the photos are going, for when the dialog is gone
   *   by the time they come.
   * @returns The chosen photographs and their dates; empty if none were
   *   chosen, or the dialog let the pick go (detach).
   */
  public showPicker(
    many = false,
    onProgress: (progress: PickProgress) => void = () => {},
    target: PickTarget = { kind: 'other' },
  ): Promise<GooglePhotoPick[]> {
    // A new tap replaces a pick still waiting.
    this.cancel();

    if (!this.clientId) {
      const error = new GooglePhotosError(
        'not-configured',
        'Google Photos is not set up in this build: VITE_GOOGLE_CLIENT_ID is missing.',
      );
      reportFailure(error, 'open');
      return Promise.reject(error);
    }

    const held = this.token && this.token.expiresAt > Date.now() ? this.token : null;
    if (held) {
      const win = openGoogleWindow('about:blank');
      if (!win) return this.blocked();
      showOpening(win);
      onProgress({ stage: 'opening', count: 0 });
      const live = this.startLive(newState(), target, many, win, onProgress);
      void this.openDirect(live, held);
      return live.promise;
    }

    const store = localStore();
    const request = { state: newState(), many, language: getLanguage(), createdAt: Date.now(), target };
    try {
      if (!store) throw new Error('localStorage is blocked');
      writeRequest(store, request);
    } catch (error) {
      const failure = new GooglePhotosError(
        'unknown',
        `Google Photos needs this site's storage to sign in, and it is blocked: ${String(error)}`,
      );
      reportFailure(failure, 'open');
      return Promise.reject(failure);
    }

    const win = openGoogleWindow(buildAuthUrl({
      clientId: this.clientId,
      redirectUri: returnUri(window.location.origin),
      state: request.state,
    }));
    if (!win) {
      clearRequest(store);
      return this.blocked();
    }
    onProgress({ stage: 'waiting', count: 0 });
    const live = this.startLive(request.state, target, many, win, onProgress);
    void this.openAfterSignIn(live, store);
    return live.promise;
  }

  /**
   * Brings in the pending pick, if there is one and no other tab is: asks
   * Google until the gardener has chosen, fetches the photos, and hands them
   * to the dialog that asked (when still open in this tab) or the fallback.
   * Safe to call at any time and as often as wanted: when the app opens,
   * when it comes back to the front, when the network returns.
   */
  public resume(): void {
    if (this.runner) return;
    const store = localStore();
    if (!store) return;
    reportStrandedFailure(store, this.live?.pickId);

    const pending = readPending(store);
    if (!pending) return;
    if (isPendingExpired(pending, Date.now())) {
      clearPending(store, pending.pickId);
      void deleteSession(boundFetch, pending.sessionId, pending.token.value);
      this.fail(pending.pickId, new GooglePhotosError('timed-out', 'The pick ran out before its photos were brought in.'), 'resume');
      return;
    }

    const claim = claimPending(store, pending.pickId, TAB_ID, Date.now());
    if (claim === 'gone') return;
    if (claim === 'held') {
      // Another tab has it. If that tab has stopped, its hold lapses: look again then.
      if (!this.retryTimer) {
        this.retryTimer = setTimeout(() => { this.retryTimer = null; this.resume(); }, GOOGLE_PHOTOS_PENDING_CLAIM_MS + 500);
      }
      return;
    }

    const runner: Runner = { pickId: pending.pickId, controller: new AbortController(), committed: false };
    this.runner = runner;
    void this.bringIn(store, pending, runner).finally(() => {
      if (this.runner !== runner) return;
      this.runner = null;
      // A new tap's pick kept while this one's photos were coming over.
      const next = readPending(store);
      if (next && next.pickId !== runner.pickId) this.resume();
    });
  }

  /**
   * Holds a tap's pick as this tab's, with the promise the dialog awaits.
   *
   * @param pickId - The pick's id.
   * @param target - Where the photos are going.
   * @param many - Whether several photos may be picked.
   * @param win - The window the tap opened.
   * @param onProgress - Told as the pick moves on.
   * @returns The pick.
   */
  private startLive(
    pickId: string,
    target: PickTarget,
    many: boolean,
    win: Window | null,
    onProgress: (progress: PickProgress) => void,
  ): LivePick {
    /** Settles the pick's promise with the photos picked (set when the promise is made). */
    let resolve: (picks: GooglePhotoPick[]) => void = () => {};
    /** Settles the pick's promise with the failure (set when the promise is made). */
    let reject: (error: unknown) => void = () => {};
    const promise = new Promise<GooglePhotoPick[]>((res, rej) => { resolve = res; reject = rej; });
    const live: LivePick = {
      pickId, target, many, controller: new AbortController(), win, promise, resolve, reject, onProgress,
      detached: false, settled: false,
    };
    this.live = live;
    return live;
  }

  /**
   * Ends a pick's promise, once.
   *
   * @param live - The pick.
   * @param outcome - The photos, or the failure.
   */
  private settle(live: LivePick, outcome: GooglePhotoPick[] | unknown): void {
    if (live.settled) return;
    live.settled = true;
    if (Array.isArray(outcome)) live.resolve(outcome as GooglePhotoPick[]);
    else live.reject(outcome);
  }

  /**
   * Stops this tab's pick: its steps, its window, and its promise (with a cancel).
   *
   * @param live - The pick.
   */
  private stopLive(live: LivePick): void {
    live.controller.abort();
    if (this.live === live) this.live = null;
    closeQuietly(live.win);
    this.settle(live, new GooglePhotosError('cancelled', 'The pick was cancelled.'));
  }

  /**
   * This tab's pick with this id, while a dialog still waits on it.
   *
   * @param pickId - The pick's id.
   * @returns The pick, or null when there is none or its dialog let it go.
   */
  private waiting(pickId: string): LivePick | null {
    const live = this.live;
    return live && live.pickId === pickId && !live.detached && !live.settled ? live : null;
  }

  /**
   * Forgets this tab's pick once it is over, and closes its window.
   *
   * @param pickId - The pick's id.
   */
  private release(pickId: string): void {
    const live = this.live;
    if (!live || live.pickId !== pickId) return;
    this.live = null;
    // Stops a sign-in wait still running for a pick already brought in.
    live.controller.abort();
    closeQuietly(live.win);
  }

  /**
   * A pick that failed: sent to Sentry, then told to its dialog, or by the
   * fallback's toast when no dialog waits (unless the gardener knows already).
   *
   * @param pickId - The pick's id.
   * @param error - Why.
   * @param step - Where it stopped.
   */
  private fail(pickId: string, error: unknown, step: PickStep): void {
    reportFailure(error, step);
    const live = this.waiting(pickId);
    this.release(pickId);
    if (live) this.settle(live, error);
    else if (!QUIET_FALLBACK.has(reasonOf(error))) this.fallback?.failed(error);
  }

  /**
   * The failure for a window the browser would not open, sent to Sentry.
   *
   * @returns A promise ending with a 'popup-blocked' error.
   */
  private blocked(): Promise<GooglePhotoPick[]> {
    const error = new GooglePhotosError(
      'popup-blocked',
      'The browser blocked the Google Photos window. Pop-ups may be blocked for this site.',
    );
    reportFailure(error, 'open');
    return Promise.reject(error);
  }

  /**
   * With a token held: makes the session, keeps the pick, sends the blank
   * window to the picker, and starts bringing it in.
   *
   * @param live - The tap's pick.
   * @param token - The held token.
   */
  private async openDirect(live: LivePick, token: HeldToken): Promise<void> {
    const { signal } = live.controller;
    let session: PickerSession;
    try {
      session = await createSession(boundFetch, token.value, live.many, signal);
    } catch (error) {
      if (signal.aborted) return;
      if (reasonOf(error) === 'signed-out') this.token = null;
      this.fail(live.pickId, error, 'session');
      return;
    }
    // Before it goes to Google the window is ours, so closed means closed.
    if (signal.aborted || !live.win || live.win.closed) {
      void deleteSession(boundFetch, session.id, token.value);
      if (!signal.aborted) this.stopLive(live);
      return;
    }
    const store = localStore();
    const kept = store !== null && writePending(store, makePending({
      pickId: live.pickId, session, token, many: live.many, target: live.target, now: Date.now(),
    }));
    if (!kept) {
      void deleteSession(boundFetch, session.id, token.value);
      this.fail(live.pickId, new GooglePhotosError('unknown', "Google Photos needs this site's storage, and it is blocked."), 'session');
      return;
    }
    live.win.location.replace(pickerAddress(session));
    live.onProgress({ stage: 'waiting', count: 0 });
    this.resume();
  }

  /**
   * Without a token: waits for the window to sign in and hand over its
   * session, then starts bringing the pick in. The window keeps the pick
   * itself (googlePhotosReturn), so another Grow tab may bring it in first.
   *
   * @param live - The tap's pick.
   * @param store - localStorage.
   */
  private async openAfterSignIn(live: LivePick, store: Storage): Promise<void> {
    const { signal } = live.controller;
    let handoff: Handoff | 'pending';
    try {
      handoff = await waitForHandoff(store, live.pickId, signal);
    } catch (error) {
      clearRequest(store);
      if (signal.aborted) return;
      this.fail(live.pickId, error, 'sign-in');
      return;
    }
    clearRequest(store);
    // Already brought in (a resume found the pick the window kept), or cancelled.
    if (this.live !== live) return;

    if (handoff !== 'pending') {
      if (!handoff.ok) {
        this.fail(live.pickId, new GooglePhotosError(handoff.reason, handoff.detail), 'sign-in');
        return;
      }
      this.token = handoff.token;
      if (readPending(store)?.pickId !== live.pickId) {
        // The window keeps the pick before it hands over. None kept means
        // another tab has brought it in or it was cancelled — unless the
        // window could not write to storage and only its message came.
        if (!writePending(store, makePending({
          pickId: live.pickId, session: handoff.session, token: handoff.token, many: live.many, target: live.target, now: Date.now(),
        }))) {
          this.fail(live.pickId, new GooglePhotosError('unknown', "Google Photos needs this site's storage, and it is blocked."), 'sign-in');
          return;
        }
      }
    }
    this.resume();
  }


  /**
   * Brings one pending pick in: asks Google until it is chosen, lists and
   * fetches the photos, hands them over, and forgets the pick. Holds the
   * pick (claimPending) between asks; when another tab holds it, or it is
   * gone, stops quietly.
   *
   * @param store - localStorage.
   * @param pending - The pick.
   * @param runner - This run, with its controller.
   */
  private async bringIn(store: Storage, pending: PendingPick, runner: Runner): Promise<void> {
    const { pickId } = pending;
    const { signal } = runner.controller;
    let step: PickStep = 'poll';
    /** Keeps the hold, or stops when it is lost. */
    const hold = () => {
      const claim = claimPending(store, pickId, TAB_ID, Date.now());
      if (claim !== 'claimed') throw new Stopped(claim);
    };
    let first = true;
    /**
     * Waits between asks, keeping the hold. The first ask is at once: a
     * resume comes when the gardener is back, the moment to ask.
     *
     * @param ms - Google's interval.
     * @param s - Cancels the wait.
     */
    const sleep = async (ms: number, s?: AbortSignal) => {
      if (first) first = false;
      else await sleepUntilBack(ms, s);
      hold();
    };

    // The next tap needs no sign-in while this token lasts.
    if (!this.token || this.token.expiresAt < pending.token.expiresAt) this.token = pending.token;

    try {
      this.waiting(pickId)?.onProgress({ stage: 'waiting', count: 0 });
      await pollUntilPicked({
        fetchFn: boundFetch,
        session: { id: pending.sessionId, pickerUri: '', pollingConfig: { pollInterval: pending.pollInterval } },
        token: pending.token.value,
        sleep,
        now: Date.now,
        signal,
        maxWaitMs: Math.max(0, Number(pending.expiresAt) - Date.now()),
      });

      step = 'list';
      const picked = await listPicked(boundFetch, pending.sessionId, pending.token.value, signal);
      hold();
      runner.committed = true;
      const wanted = pending.many ? picked : picked.slice(0, 1);
      const live = this.waiting(pickId);
      if (live) live.onProgress({ stage: 'fetching', count: wanted.length });
      else this.fallback?.adding(wanted.length, pending.target);

      // One at a time: a long batch through our server at once is a long wait
      // for every photo rather than the first.
      step = 'download';
      const fetched: GooglePhotoPick[] = [];
      let failure: unknown = null;
      for (const item of wanted) {
        if (signal.aborted) throw new GooglePhotosError('cancelled', 'The pick was cancelled.');
        try {
          fetched.push(await download(item, pending.token.value, signal));
        } catch (error) {
          failure = error;
        }
      }
      if (fetched.length === 0 && failure) throw failure;
      // Some came and some did not: the ones that came go in, and the rest is reported.
      if (failure) reportFailure(failure, 'download');

      clearPending(store, pickId);
      void deleteSession(boundFetch, pending.sessionId, pending.token.value);

      step = 'deliver';
      const receiver = this.waiting(pickId);
      this.release(pickId);
      if (receiver) {
        this.settle(receiver, fetched);
      } else if (this.fallback) {
        await this.fallback.deliver(fetched, pending.target);
      } else {
        throw new GooglePhotosError('unknown', 'The photos came with nowhere to put them.');
      }
    } catch (error) {
      if (error instanceof Stopped || signal.aborted || reasonOf(error) === 'cancelled') {
        // Another tab has it, or it was brought in or cancelled: nothing to say here.
        const live = this.waiting(pickId);
        this.release(pickId);
        if (live) this.settle(live, []);
        return;
      }
      if (reasonOf(error) === 'signed-out') this.token = null;
      // Offline, the pick is kept for when the network and the gardener come back.
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
      if (!offline || step === 'deliver') {
        clearPending(store, pickId);
        void deleteSession(boundFetch, pending.sessionId, pending.token.value);
      }
      this.fail(pickId, error, step);
    }
  }
}
